import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpDown,
  Check,
  ClipboardList,
  Filter,
  Layers,
  MapPin,
  Package,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import {
  fetchAllPaginated,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';
import { normalizeBrandLabel } from '../utils/brandFilters';
import { useSite } from '../context/SiteContext';
import { applyActiveProductsFilter } from '../utils/productStatus';

const formatCantidad = (value) => Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// Los updates de stock_levels se mandan de a tandas para no abrir 200 conexiones
// simultaneas contra Supabase cuando el ajuste toca muchos productos.
const UPDATE_BATCH_SIZE = 10;

const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

const InventarioAjusteMasivo = ({ onNavigate }) => {
  const { profile, organization } = useAuth();
  const { selectedSiteId: ctxSelectedSiteId } = useSite();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productos, setProductos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [defaultSiteId, setDefaultSiteId] = useState('');
  const [selectedSitioId, setSelectedSitioId] = useState(ctxSelectedSiteId || '');

  // Filtros del panel de seleccion
  const [query, setQuery] = useState('');
  const [marcaFiltro, setMarcaFiltro] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [soloConStock, setSoloConStock] = useState(false);

  // Lineas cargadas para ajustar: [{ key, producto, tipo, cantidad, motivo }]
  const [lineas, setLineas] = useState([]);
  const [motivoGlobal, setMotivoGlobal] = useState('');
  const [cantidadGlobal, setCantidadGlobal] = useState('');

  useEffect(() => {
    if (ctxSelectedSiteId && ctxSelectedSiteId !== selectedSitioId) {
      setSelectedSitioId(ctxSelectedSiteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxSelectedSiteId]);

  useEffect(() => {
    if (profile?.organization_id) fetchMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (selectedSitioId) persistSelectedSiteId(selectedSitioId);
  }, [selectedSitioId]);

  const fetchMasters = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;
      const resOrg = { data: organization };
      const [resSitios, resCategorias] = await Promise.all([
        supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre'),
        supabase.from('product_categories').select('id, name').eq('organization_id', orgId).order('name'),
      ]);

      // Si la organizacion todavia no tiene categorias (o la tabla no existe en
      // este entorno) seguimos sin el filtro en vez de romper la pantalla.
      if (resCategorias.error) {
        console.warn('[InventarioAjusteMasivo] categorias no disponibles', resCategorias.error);
      }
      setCategorias(resCategorias.data || []);

      const availableSites = resSitios.data || [];
      const resolvedDefaultSiteId = resolvePreferredStockSiteId(resOrg.data, availableSites);

      await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId: resolvedDefaultSiteId,
      });

      // Ver nota en InventarioAjuste: criterio unico de producto operable.
      const productosData = await fetchAllPaginated(() =>
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('*, product_categories(name), stock_levels(*), product_variants(stock_quantity)')
          .eq('organization_id', orgId)
        ).order('name', { ascending: true })
      );

      setProductos(productosData || []);
      setSitios(availableSites);
      setDefaultSiteId(resolvedDefaultSiteId || '');
      setSelectedSitioId((prev) => resolvePreferredSiteId(availableSites, { currentSiteId: prev }));
    } catch (error) {
      console.error('[InventarioAjusteMasivo] fetchMasters', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const sitioNombre = useMemo(
    () => sitios.find((site) => site.id === selectedSitioId)?.nombre || '-',
    [sitios, selectedSitioId]
  );

  const sitioParaStock = selectedSitioId || defaultSiteId || null;

  const categoriaMap = useMemo(
    () => new Map(categorias.map((categoria) => [categoria.id, categoria.name])),
    [categorias]
  );

  const nombreCategoria = (product) =>
    product?.product_categories?.name
    || categoriaMap.get(product?.category_id)
    || product?.category
    || 'Sin categoria';

  const marcas = useMemo(() => {
    const set = new Set();
    productos.forEach((product) => {
      const brand = normalizeBrandLabel(product.brand, '');
      if (brand) set.add(brand);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [productos]);

  const tiposProducto = useMemo(() => {
    const set = new Set();
    productos.forEach((product) => {
      const tipo = String(product.product_type || '').trim();
      if (tipo) set.add(tipo);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [productos]);

  const seleccionadosIds = useMemo(
    () => new Set(lineas.map((linea) => linea.producto.id)),
    [lineas]
  );

  const productosFiltrados = useMemo(() => {
    const term = query.trim().toLowerCase();
    return productos
      .filter((product) => {
        if (marcaFiltro && normalizeBrandLabel(product.brand, '') !== marcaFiltro) return false;
        if (categoriaFiltro === '__sin__') {
          if (product.category_id) return false;
        } else if (categoriaFiltro && product.category_id !== categoriaFiltro) return false;
        if (tipoFiltro && String(product.product_type || '').trim() !== tipoFiltro) return false;
        if (soloConStock && getProductStockForSite(product, sitioParaStock) <= 0) return false;
        if (!term) return true;
        const name = (product.name || '').toLowerCase();
        const barcode = (product.barcode || '').toLowerCase();
        const brand = normalizeBrandLabel(product.brand, '').toLowerCase();
        return name.includes(term) || barcode.includes(term) || brand.includes(term);
      })
      .slice(0, 120);
  }, [categoriaFiltro, marcaFiltro, productos, query, sitioParaStock, soloConStock, tipoFiltro]);

  const buildLinea = (producto) => ({
    key: producto.id,
    producto,
    tipo: 'entrada',
    cantidad: cantidadGlobal || '',
    motivo: motivoGlobal || '',
  });

  // Talles y colores: los productos con stock por talle se ajustan talle por
  // talle desde "Ajuste de stock"; acá escribiríamos el total y la base lo
  // rechaza. Los dejamos afuera con aviso claro.
  const esPorTalle = (producto) => producto?.stock_tracking === 'variant';

  const agregarProducto = (producto) => {
    if (esPorTalle(producto)) {
      toast.error(`"${producto.name}" se maneja por talle/color: ajustalo desde Ajuste de stock.`);
      return;
    }
    if (seleccionadosIds.has(producto.id)) {
      toast('Ese producto ya esta en la tabla', { icon: 'ℹ️' });
      return;
    }
    setLineas((prev) => [...prev, buildLinea(producto)]);
  };

  const agregarTodosLosFiltrados = () => {
    const porTalleOmitidos = productosFiltrados.filter((product) => esPorTalle(product) && !seleccionadosIds.has(product.id)).length;
    const nuevos = productosFiltrados.filter((product) => !seleccionadosIds.has(product.id) && !esPorTalle(product));
    if (porTalleOmitidos > 0) {
      toast(`${porTalleOmitidos} producto(s) con talles quedan afuera: se ajustan desde Ajuste de stock.`, { icon: 'ℹ️' });
    }
    if (nuevos.length === 0) {
      toast('No hay productos nuevos para agregar', { icon: 'ℹ️' });
      return;
    }
    setLineas((prev) => [...prev, ...nuevos.map(buildLinea)]);
    toast.success(`${nuevos.length} producto(s) agregado(s)`);
  };

  const quitarLinea = (key) => setLineas((prev) => prev.filter((linea) => linea.key !== key));
  const limpiarTabla = () => setLineas([]);

  const actualizarLinea = (key, patch) => {
    setLineas((prev) => prev.map((linea) => (linea.key === key ? { ...linea, ...patch } : linea)));
  };

  const aplicarTipoATodos = (tipo) => {
    if (lineas.length === 0) return;
    setLineas((prev) => prev.map((linea) => ({ ...linea, tipo })));
  };

  const aplicarCantidadATodos = () => {
    const value = Number(cantidadGlobal);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Ingresa una cantidad valida para aplicar');
      return;
    }
    setLineas((prev) => prev.map((linea) => ({ ...linea, cantidad: String(value) })));
  };

  const aplicarMotivoATodos = () => {
    if (lineas.length === 0) return;
    setLineas((prev) => prev.map((linea) => ({ ...linea, motivo: motivoGlobal })));
  };

  // Enriquecemos cada linea con el stock del sitio elegido y su validacion.
  const lineasCalculadas = useMemo(() => {
    return lineas.map((linea) => {
      const stockActual = getProductStockForSite(linea.producto, sitioParaStock);
      const cantidadNumerica = Number(linea.cantidad || 0);
      const cantidadValida = Number.isFinite(cantidadNumerica) && cantidadNumerica > 0;
      const delta = linea.tipo === 'entrada' ? cantidadNumerica : -cantidadNumerica;
      const stockResultante = stockActual + (cantidadValida ? delta : 0);
      const sinStock = linea.tipo === 'salida' && cantidadValida && cantidadNumerica > stockActual;
      return {
        ...linea,
        stockActual,
        cantidadNumerica,
        cantidadValida,
        stockResultante,
        sinStock,
        error: !cantidadValida ? 'Cantidad invalida' : sinStock ? 'Stock insuficiente' : '',
      };
    });
  }, [lineas, sitioParaStock]);

  const totales = useMemo(() => {
    const conError = lineasCalculadas.filter((linea) => linea.error).length;
    const ingresos = lineasCalculadas.filter((l) => l.tipo === 'entrada' && !l.error).length;
    const egresos = lineasCalculadas.filter((l) => l.tipo === 'salida' && !l.error).length;
    return { conError, ingresos, egresos, total: lineasCalculadas.length };
  }, [lineasCalculadas]);

  const canSubmit = Boolean(
    selectedSitioId && lineasCalculadas.length > 0 && totales.conError === 0 && !saving && !loading
  );

  const handleGuardar = async () => {
    if (!canSubmit) return;

    const confirmacion = window.confirm(
      `Vas a aplicar ${lineasCalculadas.length} ajuste(s) en la sucursal "${sitioNombre}".\n\n` +
      `Ingresos: ${totales.ingresos}\nEgresos: ${totales.egresos}\n\n` +
      'Los ajustes impactan el stock de forma inmediata. Confirmas?'
    );
    if (!confirmacion) return;

    setSaving(true);
    try {
      const orgId = profile.organization_id;
      const productIds = lineasCalculadas.map((linea) => linea.producto.id);

      // 1) Releemos el stock real del sitio justo antes de escribir (mitiga TOCTOU).
      const { data: levels, error: levelsError } = await supabase
        .from('stock_levels')
        .select('id, product_id, quantity')
        .eq('site_id', selectedSitioId)
        .in('product_id', productIds);
      if (levelsError) throw levelsError;

      // Puede haber mas de una fila historica por (producto, sitio): sumamos para
      // validar y aplicamos el delta sobre la primera fila.
      const levelMap = new Map();
      (levels || []).forEach((row) => {
        const key = String(row.product_id);
        const previo = levelMap.get(key);
        if (previo) {
          previo.total += Number(row.quantity || 0);
        } else {
          levelMap.set(key, {
            id: row.id,
            base: Number(row.quantity || 0),
            total: Number(row.quantity || 0),
          });
        }
      });

      const faltantes = lineasCalculadas.filter((linea) => {
        if (linea.tipo !== 'salida') return false;
        const level = levelMap.get(String(linea.producto.id));
        return Number(level?.total || 0) < linea.cantidadNumerica;
      });

      if (faltantes.length > 0) {
        const detalle = faltantes
          .slice(0, 3)
          .map((linea) => linea.producto.name)
          .join(', ');
        toast.error(
          `Stock insuficiente al revalidar en ${detalle}${faltantes.length > 3 ? ` y ${faltantes.length - 3} mas` : ''}`
        );
        await fetchMasters();
        return;
      }

      // 2) Escritura de stock_levels: update para lo existente, insert para lo nuevo.
      const updates = [];
      const inserts = [];
      lineasCalculadas.forEach((linea) => {
        const delta = linea.tipo === 'entrada' ? linea.cantidadNumerica : -linea.cantidadNumerica;
        const level = levelMap.get(String(linea.producto.id));
        if (level?.id) {
          updates.push({ id: level.id, quantity: level.base + delta });
        } else {
          inserts.push({
            organization_id: orgId,
            product_id: linea.producto.id,
            site_id: selectedSitioId,
            quantity: delta,
          });
        }
      });

      const errores = [];
      for (const tanda of chunk(updates, UPDATE_BATCH_SIZE)) {
        const resultados = await Promise.all(
          tanda.map((row) =>
            supabase
              .from('stock_levels')
              .update({ quantity: row.quantity, updated_at: new Date().toISOString() })
              .eq('id', row.id)
          )
        );
        resultados.forEach((res, index) => {
          if (res.error) errores.push({ id: tanda[index].id, error: res.error });
        });
      }

      if (inserts.length > 0) {
        const { error: insertError } = await supabase.from('stock_levels').insert(inserts);
        if (insertError) errores.push({ id: 'insert', error: insertError });
      }

      // 3) Movimientos: un unico insert con todas las lineas aplicadas.
      const movimientos = lineasCalculadas.map((linea) => {
        const motivoFinal = linea.motivo?.trim()
          ? `Ajuste masivo: ${linea.motivo.trim()}`
          : 'Ajuste masivo';
        return {
          organization_id: orgId,
          producto_id: linea.producto.id,
          tipo: linea.tipo === 'entrada' ? 'Ingreso' : 'Egreso',
          cantidad: linea.cantidadNumerica,
          motivo: motivoFinal,
          sitio_origen: linea.tipo === 'salida' ? selectedSitioId : null,
          sitio_destino: linea.tipo === 'entrada' ? selectedSitioId : null,
        };
      });

      const { error: movementError } = await supabase.from('movimientos_stock').insert(movimientos);
      if (movementError) {
        console.error('[InventarioAjusteMasivo] movimientos', movementError);
      }

      if (errores.length > 0) {
        console.error('[InventarioAjusteMasivo] errores de escritura', errores);
        toast.error(`${errores.length} ajuste(s) no se pudieron aplicar. Revisa y reintenta.`);
      } else {
        toast.success(`${lineasCalculadas.length} ajuste(s) aplicados`);
        setLineas([]);
      }

      await fetchMasters();
    } catch (error) {
      console.error('[InventarioAjusteMasivo] handleGuardar', error);
      toast.error('Error al procesar los ajustes');
    } finally {
      setSaving(false);
    }
  };

  const volver = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('inventario-ajuste');
      return;
    }
    navigate('/sistema/inventario-ajuste');
  };

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Layers size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Ajuste masivo
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Filtra, carga productos y ajusta el stock en una sola pasada
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={volver}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Ajuste simple
          </button>
          <button
            type="button"
            onClick={limpiarTabla}
            disabled={lineas.length === 0 || saving}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            <X size={14} /> Vaciar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={!canSubmit}
            className="px-5 py-2.5 rounded-2xl bg-[#0B2A36] text-white shadow-lg hover:shadow-xl hover:opacity-95 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCcw size={16} className="animate-spin" /> Procesando
              </>
            ) : (
              <>
                <Save size={16} strokeWidth={3} className="text-[#3FF2AA]" /> Confirmar {lineas.length > 0 ? `(${lineas.length})` : ''}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3 min-w-0">
          <div className="p-2.5 rounded-xl bg-slate-50 text-slate-500 shadow-inner shrink-0">
            <MapPin size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Sucursal</p>
            <select
              value={selectedSitioId}
              onChange={(event) => setSelectedSitioId(event.target.value)}
              className="w-full bg-transparent text-[12px] font-black text-slate-800 uppercase tracking-tight outline-none cursor-pointer"
            >
              <option value="">Seleccionar...</option>
              {sitios.map((site) => (
                <option key={site.id} value={site.id}>{site.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-50 text-[#51A7FD] shadow-inner shrink-0">
            <ClipboardList size={20} />
          </div>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Lineas cargadas</p>
            <h3 className="text-[14px] font-black tracking-tight text-slate-800">{totales.total}</h3>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-700 shadow-inner shrink-0">
            <ArrowUpDown size={20} />
          </div>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ingresos / Egresos</p>
            <h3 className="text-[14px] font-black tracking-tight text-slate-800">
              {totales.ingresos} / {totales.egresos}
            </h3>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${totales.conError > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-500'} shadow-inner shrink-0`}>
            <AlertCircle size={20} />
          </div>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Con error</p>
            <h3 className={`text-[14px] font-black tracking-tight ${totales.conError > 0 ? 'text-rose-700' : 'text-slate-800'}`}>
              {totales.conError}
            </h3>
          </div>
        </div>
      </div>

      {/* PANEL DE SELECCION */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-3">
        <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Filter size={14} className="text-[#51A7FD]" /> Filtrar y seleccionar productos
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="BUSCAR NOMBRE, MARCA O CODIGO..."
              className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase"
            />
          </div>

          <div className="md:col-span-3">
            <select
              value={categoriaFiltro}
              onChange={(event) => setCategoriaFiltro(event.target.value)}
              disabled={categorias.length === 0}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer disabled:opacity-50"
            >
              <option value="">Todas las categorias</option>
              <option value="__sin__">Sin categoria</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>{categoria.name}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <select
              value={marcaFiltro}
              onChange={(event) => setMarcaFiltro(event.target.value)}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Todas las marcas</option>
              {marcas.map((marca) => (
                <option key={marca} value={marca}>{marca}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <select
              value={tipoFiltro}
              onChange={(event) => setTipoFiltro(event.target.value)}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Todo tipo</option>
              {tiposProducto.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>

        </div>

        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={soloConStock}
                onChange={(event) => setSoloConStock(event.target.checked)}
                className="accent-[#51A7FD]"
              />
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Solo con stock</span>
            </label>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              {loading ? 'Cargando productos...' : `${productosFiltrados.length} resultado(s) · maximo 120 en pantalla`}
            </p>
          </div>
          <button
            type="button"
            onClick={agregarTodosLosFiltrados}
            disabled={productosFiltrados.length === 0}
            className="px-4 py-2 rounded-xl bg-blue-50 text-[#51A7FD] border border-blue-100 text-[9px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-blue-100 transition-all disabled:opacity-50"
          >
            <Plus size={12} /> Agregar los filtrados
          </button>
        </div>

        <div className="mt-3 max-h-64 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
          {productosFiltrados.map((product) => {
            const yaEsta = seleccionadosIds.has(product.id);
            return (
              <button
                key={product.id}
                type="button"
                onClick={() => agregarProducto(product)}
                disabled={yaEsta}
                className={`w-full text-left px-4 py-2.5 transition-all flex items-center justify-between gap-3 ${
                  yaEsta ? 'bg-emerald-50/60 cursor-default' : 'hover:bg-blue-50'
                }`}
              >
                <div className="min-w-0">
                  <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                    {product.name}
                  </div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                    {normalizeBrandLabel(product.brand)} · {nombreCategoria(product)} · {product.barcode || 'S/COD'}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[10px] font-black text-[#51A7FD]">
                    {formatCantidad(getProductStockForSite(product, sitioParaStock))} {product.unit_of_measure || 'un'}
                  </span>
                  {yaEsta ? (
                    <Check size={14} className="text-emerald-600" />
                  ) : (
                    <Plus size={14} className="text-slate-300" />
                  )}
                </div>
              </button>
            );
          })}

          {!loading && productosFiltrados.length === 0 && (
            <div className="px-4 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
              Sin resultados
            </div>
          )}
        </div>
      </div>

      {/* TABLA DE AJUSTE */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <Package size={14} className="text-[#51A7FD]" /> Productos a ajustar
          </h2>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => aplicarTipoATodos('entrada')}
              disabled={lineas.length === 0}
              className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              Todo ingreso
            </button>
            <button
              type="button"
              onClick={() => aplicarTipoATodos('salida')}
              disabled={lineas.length === 0}
              className="px-3 py-2 rounded-xl bg-rose-50 text-rose-700 border border-rose-100 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              Todo egreso
            </button>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.01"
                min="0"
                value={cantidadGlobal}
                onChange={(event) => setCantidadGlobal(event.target.value)}
                placeholder="CANT."
                className="w-20 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-black text-right outline-none focus:border-[#51A7FD]"
              />
              <button
                type="button"
                onClick={aplicarCantidadATodos}
                disabled={lineas.length === 0}
                className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 border border-slate-200 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                Aplicar
              </button>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={motivoGlobal}
                onChange={(event) => setMotivoGlobal(event.target.value.toUpperCase())}
                placeholder="MOTIVO GENERAL"
                className="w-40 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD]"
              />
              <button
                type="button"
                onClick={aplicarMotivoATodos}
                disabled={lineas.length === 0}
                className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 border border-slate-200 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        {lineasCalculadas.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">
              Elegi productos del panel de arriba para armar el ajuste
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead className="bg-slate-50">
                <tr className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                  <th className="text-left px-4 py-3">Producto</th>
                  <th className="text-right px-3 py-3">Stock actual</th>
                  <th className="text-center px-3 py-3">Movimiento</th>
                  <th className="text-right px-3 py-3">Cantidad</th>
                  <th className="text-right px-3 py-3">Stock resultante</th>
                  <th className="text-left px-3 py-3">Motivo</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {lineasCalculadas.map((linea) => (
                  <tr key={linea.key} className={linea.error ? 'bg-rose-50/40' : ''}>
                    <td className="px-4 py-2.5 min-w-[220px]">
                      <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                        {linea.producto.name}
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                        {normalizeBrandLabel(linea.producto.brand)} · {nombreCategoria(linea.producto)} · {linea.producto.barcode || 'S/COD'}
                      </div>
                      {linea.error && (
                        <div className="text-[9px] font-black uppercase tracking-widest text-rose-600 mt-1">
                          {linea.error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-600 whitespace-nowrap">
                      {formatCantidad(linea.stockActual)} {linea.producto.unit_of_measure || 'un'}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 w-[150px] mx-auto">
                        <button
                          type="button"
                          onClick={() => actualizarLinea(linea.key, { tipo: 'entrada' })}
                          className={`flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${
                            linea.tipo === 'entrada' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-400'
                          }`}
                        >
                          Ingreso
                        </button>
                        <button
                          type="button"
                          onClick={() => actualizarLinea(linea.key, { tipo: 'salida' })}
                          className={`flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${
                            linea.tipo === 'salida' ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-400'
                          }`}
                        >
                          Egreso
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={linea.cantidad}
                        onFocus={(event) => event.target.select()}
                        onChange={(event) => actualizarLinea(linea.key, { cantidad: event.target.value })}
                        placeholder="0.00"
                        className={`w-24 bg-slate-50 border px-2 py-2 rounded-xl text-[11px] font-black text-right outline-none focus:border-[#51A7FD] ${
                          linea.error ? 'border-rose-200' : 'border-slate-100'
                        }`}
                      />
                    </td>
                    <td className={`px-3 py-2.5 text-right text-[11px] font-black whitespace-nowrap ${
                      linea.stockResultante < 0 ? 'text-rose-600' : 'text-slate-700'
                    }`}>
                      {formatCantidad(linea.stockResultante)}
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        type="text"
                        value={linea.motivo}
                        onChange={(event) => actualizarLinea(linea.key, { motivo: event.target.value.toUpperCase() })}
                        placeholder="EJ: CONTEO"
                        className="w-40 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD]"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => quitarLinea(linea.key)}
                        className="p-2 rounded-xl text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all"
                        title="Quitar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InventarioAjusteMasivo;
